// @flow

import * as React from 'react';
import {Bar} from 'react-chartjs-2';

import type {CalculationResultDetails, CalculationResultDetailsBySource} from "../common/RequestTypes";
import ColorManager from "../common/ColorManager";
import type {Color} from "../common/ColorManager";
import OptionManager from "../common/OptionManager";
import ReactAI from 'react-appinsights';

type Props = {
    calculationResultDetails: CalculationResultDetails,
    techniquesToCompare: number,
    optionManager: OptionManager,
    unit: string
};


class TotalClimateEffectWithDetailsChart extends React.Component<Props> {

    colorManager: ColorManager;

    constructor() {
        super();
        this.colorManager = new ColorManager();
    }

    render() {

        const options = {
            scales: {
                xAxes: [{
                    stacked: true,
                }],
                yAxes: [{
                    stacked: true,
                    scaleLabel: {
                        display: true,
                        labelString: this.props.unit,
                    },
                    ticks: {
                        beginAtZero: true
                    }
                }]
            },
            legend: {
                // position: 'right'
                display: false
            },
            tooltips: {
                callbacks: {
                    label: (tooltipItem, data) => {
                        let techName = data.datasets[tooltipItem.datasetIndex].label;
                        let formattedValue = parseFloat(tooltipItem.yLabel.toFixed(0));
                        return `${techName}: ${formattedValue} ${this.props.unit}`;
                    }
                }
            }
        };

        // Every dataset corresponds to a source.
        // Only add sources where total for all techs > 0
        // Only add used techs
        let datasets = [];
        
        let totalsPerSource = this.getTotalsPerSource(this.props.calculationResultDetails);

        if(totalsPerSource.tubeMaterial !== 0) {
            let sourceId = 'tubeMaterial';
            let sourceName = 'Rörmaterial';
            datasets = [...datasets, this.createDataSet(this.props.calculationResultDetails, sourceName, sourceId, this.colorManager.colorTubeMaterial)];
        }
        if(totalsPerSource.filling !== 0) {
            let sourceId = 'filling';
            let sourceName = 'Fyllnadsmassor';
            datasets = [...datasets, this.createDataSet(this.props.calculationResultDetails, sourceName, sourceId, this.colorManager.colorFilling)];
        }
        if(totalsPerSource.asphalt !== 0) {
            let sourceId = 'asphalt';
            let sourceName = 'Asfalt';
            datasets = [...datasets, this.createDataSet(this.props.calculationResultDetails, sourceName, sourceId, this.colorManager.colorAsphalt)];
        }
        if(totalsPerSource.energy !== 0) {
            let sourceId = 'energy';
            let sourceName = 'Energi';
            datasets = [...datasets, this.createDataSet(this.props.calculationResultDetails, sourceName, sourceId, this.colorManager.colorEnergy)];
        }
        if(totalsPerSource.transports !== 0) {
            let sourceId = 'transports'; 
            let sourceName = 'Transporter';
            datasets = [...datasets, this.createDataSet(this.props.calculationResultDetails, sourceName, sourceId, this.colorManager.colorTransports)];
        }
        if(totalsPerSource.waste !== 0) {
            let sourceId = 'waste'; 
            let sourceName = 'Avfall';
            datasets = [...datasets, this.createDataSet(this.props.calculationResultDetails, sourceName, sourceId, this.colorManager.colorWaste)];
        }
        if(totalsPerSource.recyclePotential !== 0) {
            let sourceId = 'recyclePotential'; 
            let sourceName = 'Återvinningspotential';
            datasets = [...datasets, this.createDataSet(this.props.calculationResultDetails, sourceName, sourceId, this.colorManager.colorRecycle)];
        }
        
        const labels = this.getLabels();
        
        let data = {
            datasets,
            labels: labels,
        };

        return (
            <Bar
                data={data}
                options={options}
            />
        );
    }

    createDataSet(resultDetails: CalculationResultDetails, sourceName: string, sourceId: string, color: Color) {
        let dataSet = {
            label: sourceName,
            data: this.getDataArrayForSource(resultDetails, sourceId),
            stack: "Stack",
        };
        
        let colorProperties = this.colorManager.getChartColorProperties(color);
        return {...dataSet, ...colorProperties};
    }
    
    getDataArrayForSource(resultDetails: CalculationResultDetails, sourceId: string): number[] {
        let retVal = []; 
        if(this.props.optionManager.isInsertionFlexibleLiningUsed(this.props.techniquesToCompare)) {
            retVal.push(resultDetails.insertionFlexibleLining[sourceId]);       
        }
        if(this.props.optionManager.isTubeCrackingUsed(this.props.techniquesToCompare)) {
            retVal.push(resultDetails.tubeCracking[sourceId]);       
        }
        if(this.props.optionManager.isDirectedDrillingUsed(this.props.techniquesToCompare)) {
            retVal.push(resultDetails.directedDrilling[sourceId]);       
        }
        if(this.props.optionManager.isSlipliningUsed(this.props.techniquesToCompare)) {
            retVal.push(resultDetails.slipLining[sourceId]);       
        }
        if(this.props.optionManager.isPuLiningUsed(this.props.techniquesToCompare)) {
            retVal.push(resultDetails.puLiner[sourceId]);       
        }

        retVal.push(resultDetails.traditionalDigging[sourceId]);

        return retVal;
    }
    
    getLabels() {
        let labels = [];
        if(this.props.optionManager.isInsertionFlexibleLiningUsed(this.props.techniquesToCompare)) {
            labels.push('Infodring flexibla foder');
        }
        if(this.props.optionManager.isTubeCrackingUsed(this.props.techniquesToCompare)) {
            labels.push('Rörspräckning');
        }
        if(this.props.optionManager.isDirectedDrillingUsed(this.props.techniquesToCompare)) {
            labels.push('Styrd borrning');
        }
        if(this.props.optionManager.isSlipliningUsed(this.props.techniquesToCompare)) {
            labels.push('Sliplining');
        }
        if(this.props.optionManager.isPuLiningUsed(this.props.techniquesToCompare)) {
            labels.push('PU liner');
        }
        
        labels.push('Traditionell grävning');
        
        return labels;
    }
    
    getTotalsPerSource(resultDetails: CalculationResultDetails): CalculationResultDetailsBySource {
        return {
            tubeMaterial: this.getTotalBySource(resultDetails, 'tubeMaterial'),
            filling: this.getTotalBySource(resultDetails, 'filling'),
            asphalt: this.getTotalBySource(resultDetails, 'asphalt'),
            energy: this.getTotalBySource(resultDetails, 'energy'),
            transports: this.getTotalBySource(resultDetails, 'transports'),
            waste: this.getTotalBySource(resultDetails, 'waste'),
            // total: this.getTotalBySource(resultDetails, 'total'),
            totalDerived: this.getTotalBySource(resultDetails, 'totalDerived'),
            recyclePotential: this.getTotalBySource(resultDetails, 'recyclePotential'),
        }
    }

    getTotalBySource(resultDetails: CalculationResultDetails, sourceName: string): number {
        let totalBySource = 0;
        for(let techDetails of Object.values(resultDetails)) {
            // $FlowFixMe 
           if(typeof techDetails[sourceName] === 'number') 
                totalBySource += techDetails[sourceName];
        }
        return totalBySource;
    }

}

export default ReactAI.withTracking(TotalClimateEffectWithDetailsChart);





// WEBPACK FOOTER //
// ./src/components/TotalClimateEffectWithDetailsChart.js